import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AccessLevel, AuthResponse } from '../models/auth.model';

/**
 * AuthService
 * - Holds JWT session (token/expiry/accessLevel) as Signals
 * - Persists session to localStorage (SSR-safe)
 * - Can fetch current session state from backend
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly #http = inject(HttpClient);
  readonly serverURL = environment.apiUrl;

  // WHY: Keep internal domain model separate from raw API to allow backend evolution.
  readonly #token = signal<string | null>(null);
  readonly #expiresAt = signal<Date | null>(null);
  readonly #accessLevel = signal<AccessLevel | null>(null);

  // Derived authentication state; recalculates only when token/expiry change.
  readonly isAuthenticated = computed(() => {
    const token = this.#token();
    const exp = this.#expiresAt();
    const result = !!token && !!exp
    const extendedResult = result && exp.getTime() >= Date.now();
    console.log('AuthService: isAuthenticated', { result, extendedResult, time: exp?.getTime(), dat: Date.now() });
    return result;
  });

  readonly accessLevel = computed(() => this.#accessLevel());
  readonly hasFullAccess = computed(() => this.#accessLevel() === 'full');
  readonly hasPolterabendAccess = computed(() => {
    const lvl = this.#accessLevel();
    return lvl === 'full' || lvl === 'polterabend';
  });

  constructor() {
    // WHY: Lazy hydration from localStorage to preserve session across reloads without server call.
    const stored = safeReadStorage();
    if (stored && stored.expiresAt.getTime() > Date.now()) {
      this.#token.set(stored.token);
      this.#expiresAt.set(stored.expiresAt);
      this.#accessLevel.set(stored.accessLevel);
    } else if (stored) {
      // WHY: Expired session should be purged immediately to avoid flicker of logged-in UI.
      safeClearStorage();
    }


  }
  updateTokenOnAuthenticationChange = effect(() => {
    if (this.isAuthenticated()) {
      safeWriteStorage({
        token: this.#token()!,
        expiresAt: this.#expiresAt()!,
        accessLevel: this.#accessLevel()!
      });
    } else {
      safeClearStorage();
    }
  });

  login(password: string) {
    return this.#http.post<AuthResponse>(`${this.serverURL}/auth`, { password }).pipe(
      tap((response) => this.setSession(response)),
      catchError((error) => {
        this.logout();
        return throwError(() => error);
      })
    );
  }

  private setSession(resp: AuthResponse) {
    if (resp.valid && resp.token && resp.expires_at && resp.access_level) {
      const session = mapApiResponse(resp);
      console.log("session", session)
      this.#token.set(session.token);
      this.#expiresAt.set(session.expiresAt);
      this.#accessLevel.set(session.accessLevel);
    }
  }

  logout() {
    this.#token.set(null);
    this.#expiresAt.set(null);
    this.#accessLevel.set(null);
  }
}

// ---- Functional helpers (pure) ----
interface SessionModel { token: string; expiresAt: Date; accessLevel: AccessLevel; }

function mapApiResponse(resp: AuthResponse): SessionModel {
  return {
    token: resp.token!,
    expiresAt: new Date(resp.expires_at!),
    accessLevel: resp.access_level! as AccessLevel
  };
}

function safeReadStorage(): SessionModel | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  const raw = localStorage.getItem('authSession');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { token: string; expiresAt: string; accessLevel: AccessLevel };
    return {
      token: parsed.token,
      expiresAt: new Date(parsed.expiresAt),
      accessLevel: parsed.accessLevel
    };
  } catch { return null; }
}

function safeWriteStorage(session: SessionModel): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  localStorage.setItem('authSession', JSON.stringify({
    token: session.token,
    expiresAt: session.expiresAt.toISOString(),
    accessLevel: session.accessLevel
  }));
}

function safeClearStorage(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  localStorage.removeItem('authSession');
}
